import React, { useRef } from "react";
import { Box, Typography, TextField, IconButton } from "@mui/material";
import SendIcon from "@mui/icons-material/Send"; // Import Send icon
import { MessageData } from "./Messages"; // Import the MessageData interface
import Message from "./Message"; // Import the new Message component
import submitNotification from "../Notifications/Util/NotificationHandler";

interface ChatboxProps {
  selectedThreadId: number; // Keep this for backward compatibility
  messages: MessageData[];
  onSendMessage: (messageContent: string, attachmentUrl: string | null) => void;
  onEditMessage: (
    IdToEdit: number | null,
    DateToEdit: string | null,
    newMessageContent: string
  ) => void;
  onDeleteMessage: (
    IdToDelete: number | null,
    DateToDelete: string | null
  ) => void;
  loading: boolean;
  error: string | null;
  currentUserId: number;
}

const Chatbox: React.FC<ChatboxProps> = ({
  messages,
  onSendMessage,
  onEditMessage,
  onDeleteMessage,
  loading,
  error,
  currentUserId,
}) => {
  const [message, setMessage] = React.useState(""); // State for the message input
  const [attachmentUrl, _setAttachmentUrl] = React.useState<string | null>(
    null
  ); // State for the attachment URL
  const messagesEndRef = useRef<HTMLDivElement | null>(null); // Ref for scrolling to the bottom

  const handleSend = async () => {
    onSendMessage(message, attachmentUrl); // Pass the attachment URL if applicable
    // Send notification after sending the message
    console.log("Sending notification");

    // Call the utility function to submit the notification
    await submitNotification(
      "109", // User ID
      "message", // Notification type
      `Angela has sent you a message.` // Message content
    );

    setMessage(""); // Clear the input after sending
  };

  // Scroll to the bottom of the chatbox
  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  // Effect to scroll to the bottom when messages change
  React.useEffect(() => {
    scrollToBottom();
  }, [messages]); // Run when messages change

  return (
    <Box
      bgcolor="background.paper"
      borderRadius={1}
      boxShadow={1}
      display="flex"
      flexDirection="column"
      height="85vh"
      position="relative"
    >
      <Box flexGrow={1} p={2} overflow="auto" pb={8}>
        {loading ? (
          <Typography>Loading messages...</Typography>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : (
          messages.map((msg, index) => (
            <Message
              key={msg.id || index}
              messageData={msg}
              onEditMessage={onEditMessage}
              onDeleteMessage={onDeleteMessage}
              currentUserId={currentUserId}
            />
          ))
        )}
        <div ref={messagesEndRef} />
      </Box>
      <Box
        component="form"
        display="flex"
        alignItems="center"
        p={1}
        bgcolor="background.paper"
        borderTop={`1px solid divider`}
        position="absolute"
        bottom={0}
        left={0}
        right={0}
        zIndex={1}
      >
        <TextField
          variant="outlined"
          fullWidth
          placeholder="Type a message..."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          sx={{ borderRadius: 20 }}
        />
        <IconButton onClick={handleSend} color="primary">
          <SendIcon />
        </IconButton>
      </Box>
    </Box>
  );
};

export default Chatbox;
